import { useState, useEffect } from 'react';
import { DropdownMenuItem } from '@components';
import { t } from 'i18next';
import { ReportFilters } from '@views/pages/Reporting/Reports/types.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReportStore } from '@store';
import { useReportTableFiltersStore } from '@store';
import { isEqual } from 'lodash';
import { useReportsQuery } from '@common/cache';
import { useQueryClient } from '@tanstack/react-query';

const emptyFilters: ReportFilters = {
  search: '',
  page: 1,
  limit: 100,
  sort_by: 'name',
  sort_order: 'ASC',
  organization_id: '',
  report_folder_id: '',
  tabIndex: '1',
};

export const useReporting = () => {
  const { pathname } = useLocation();
  const { filters: storeFilters, setFilter: setStoreFilters } =
    useReportTableFiltersStore();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ReportFilters>({
    ...storeFilters,
    tabIndex: '1',
  });

  // Invalidate recently_viewed cache every time the user lands on the Reporting page
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, []);
  const [isAddReportFolderModalOpen, setIsAddReportFolderModalOpen] =
    useState(false);
  const navigate = useNavigate();
  const { clearAll } = useReportStore();

  const isReportingSelected = pathname === '/reporting/reports';

  const isInitialFilters = isEqual(
    { ...filters, tabIndex: '1' },
    { ...emptyFilters, tabIndex: '1' },
  );

  const {
    data: favoriteCount = {
      total: 0,
    },
  } = useReportsQuery({
    ...emptyFilters,
    favorites_only: true,
  });

  const {
    data: allCount = {
      total: 0,
    },
  } = useReportsQuery({
    ...emptyFilters,
  });

  const {
    data: createdByCount = {
      total: 0,
    },
  } = useReportsQuery({
    ...emptyFilters,
    created_by_me: true,
  });

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      search: '',
      tabIndex: newValue,
      page: 1,
    }));
    setStoreFilters({ ...storeFilters, tabIndex: newValue, page: 1 });
  };

  const handleFilterChange = (newFilters: Partial<ReportFilters>) => {
    setFilters({ ...filters, page: 1, ...newFilters });
    setStoreFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleResetFilters = () => {
    setFilters({ ...emptyFilters, tabIndex: filters.tabIndex });
    setStoreFilters({ ...emptyFilters, tabIndex: filters.tabIndex });
  };

  const handleCloseAddReportFolderModal = () => {
    setIsAddReportFolderModalOpen(false);
  };

  const dropdownMenu: DropdownMenuItem[] = [
    {
      iconName: 'report',
      label: t('reporting.dropdown.report'),
      onClick: () => {
        clearAll();
        navigate('/reporting/create');
      },
    },
    {
      iconName: 'folder',
      label: t('reporting.dropdown.folder'),
      onClick: () => setIsAddReportFolderModalOpen(true),
    },
  ];

  return {
    handleChangeTab,
    dropdownMenu,
    filters,
    handleFilterChange,
    handleResetFilters,
    isReportingSelected,
    isAddReportFolderModalOpen,
    handleCloseAddReportFolderModal,
    isInitialFilters,
    favoriteCount: favoriteCount.total,
    allCount: allCount.total,
    createdByCount: createdByCount.total,
  };
};
